import { Controller } from "@hotwired/stimulus"

// One panel for every manager-approved action on a check: comping or voiding a
// line, or voiding the whole check.
//
// It is shared rather than rendered per line because it used to be per line,
// and that did not scale. Each panel mounted three controllers, so a check with
// N lines mounted 3(2N+1) of them, and every Turbo morph refresh tore the lot
// down and reconnected them. The churn detached nodes mid-tap — a second press
// landing on an element that no longer existed. One panel is a fixed cost no
// matter how long the check runs.
//
// The line buttons are plain triggers carrying what the action needs as
// Stimulus action params. Opening writes those onto the panel's own controls,
// so the approval keypad and the eventual POST are aimed at whatever was
// tapped last.
//
// Two steps, deliberately: the manager punching in a PIN should be able to see
// what they are approving, so the reason and the quantity are collected first,
// and the reason is what ends up on the report against their name.
export default class extends Controller {
  static targets = ["panel", "title", "note", "input", "confirm", "quantity", "quantityRow", "quantityLabel", "amount", "amountRow"]

  connect() {
    this.boundKey = this.#handleKey.bind(this)
  }

  disconnect() {
    window.removeEventListener("keydown", this.boundKey)
  }

  // Params: url, approvalAction, sgid, label, subject, max, amount.
  open({ params }) {
    this.max = parseInt(params.max, 10) || 1

    this.titleTarget.textContent = `${params.label} ${params.subject}`

    // What this destroys, for the actions that destroy more than they look
    // like they do. Voiding a whole check takes its rounds and its payments
    // with it, and the panel saying only "Void the whole check" does not
    // convey that.
    this.noteTarget.textContent = params.note || ""
    this.noteTarget.classList.toggle("hidden", !params.note)
    this.inputTarget.value = ""
    this.inputTarget.placeholder = params.prompt
    this.confirmTarget.textContent = params.label

    // The POST target and the approval it needs are per-action, so they are
    // rewritten here rather than baked into the markup.
    this.element.dataset.posSensitiveActionUrlValue = params.url
    this.confirmTarget.dataset.managerApprovalActionValue = params.approvalAction
    this.confirmTarget.dataset.managerApprovalTargetSgidValue = params.sgid

    // Only offer a count when there is a choice. A single-quantity line has
    // nothing to step through, and a stepper stuck at 1 reads as broken.
    this.quantityRowTarget.classList.toggle("hidden", this.max < 2)
    this.#setQuantity(this.max)

    // Money, for the actions that hand some back. Absent for everything else,
    // so the row only exists where it means something rather than sitting
    // there empty asking to be filled in.
    // Formatted here, not in the template: Stimulus parses action params as
    // JSON, so a "3762.50" written in ERB arrives as the number 3762.5 and
    // renders in the field a trailing zero short. Money is only ever shown to
    // two places.
    const wantsAmount = params.amount !== undefined && params.amount !== null
    this.amountRowTarget.classList.toggle("hidden", !wantsAmount)
    this.amountTarget.disabled = !wantsAmount
    this.amountTarget.value = wantsAmount ? Number(params.amount).toFixed(2) : ""

    this.panelTarget.classList.remove("hidden")
    this.#syncConfirm()
    this.inputTarget.focus()
    window.addEventListener("keydown", this.boundKey)
  }

  cancel(event) {
    event?.preventDefault()
    this.panelTarget.classList.add("hidden")
    this.inputTarget.value = ""
    window.removeEventListener("keydown", this.boundKey)
  }

  reasonChanged() {
    this.#syncConfirm()
  }

  more(event) {
    event?.preventDefault()
    this.#setQuantity(this.#quantity + 1)
  }

  fewer(event) {
    event?.preventDefault()
    this.#setQuantity(this.#quantity - 1)
  }

  get #quantity() {
    return parseInt(this.quantityTarget.value, 10) || 1
  }

  #setQuantity(next) {
    const bounded = Math.min(Math.max(next, 1), this.max)
    this.quantityTarget.value = bounded
    this.quantityLabelTarget.textContent = `${bounded} of ${this.max}`
  }

  // Disabled until there is a reason, so the approval keypad never appears for
  // something the server would refuse for want of one.
  #syncConfirm() {
    this.confirmTarget.disabled = this.inputTarget.value.trim() === ""
  }

  #handleKey(e) {
    if (e.key === "Escape") this.cancel(e)
  }
}
